import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import NotificationBell from './NotificationBell';
import {
  LogOut, Coins, Settings, Menu, X, Sparkles, Crown, Star, Sun, Moon, ChevronDown,
} from 'lucide-react';

const NAV_LINKS = [
  { path: '/dashboard', label: 'Overview' },
  { path: '/courses', label: 'Courses' },
  { path: '/h2h', label: '1v1' },
  { path: '/quiz-pools', label: 'Pools' },
  { path: '/daily-mocks', label: 'Mocks' },
  { path: '/performance', label: 'Report' },
  { path: '/mock-tests', label: 'Rank' },
  { path: '/doubt-solver', label: 'Doubts' },
  { path: '/rewards', label: 'Rewards' },
];

const Header = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const menuRef = useRef(null);
  const profileRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    setMobileOpen(false);
    setProfileOpen(false);
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMobileOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  const isPro = user?.subscription_status === 'active' || user?.role === 'admin';

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl border-b"
      style={{
        background: 'rgb(var(--bg) / 0.75)',
        borderColor: 'rgb(var(--border))',
      }}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2.5 shrink-0 group" data-testid="logo-link">
            <img
              src="/aptivon-logo-v2.png"
              alt="Aptivon"
              className="h-10 sm:h-11 w-auto transition-transform group-hover:scale-105"
            />
            <span className="hidden sm:block text-fg-faint text-[10px] uppercase tracking-widest">JEE · NEET OS</span>
          </Link>

          {/* Desktop Nav */}
          {user && (
            <nav className="hidden lg:flex items-center gap-0.5" data-testid="main-nav">
              {NAV_LINKS.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-md text-[13px] font-medium transition-all ${
                    isActive(link.path)
                      ? 'text-fg'
                      : 'text-fg-muted hover:text-fg'
                  }`}
                  style={
                    isActive(link.path)
                      ? { background: 'rgba(239, 68, 68, 0.12)', color: 'rgb(var(--accent))' }
                      : undefined
                  }
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
              {user.role === 'admin' && (
                <Link
                  to="/admin"
                  className={`px-3 py-2 rounded-md text-[13px] font-medium transition-all flex items-center gap-1 ${
                    isActive('/admin') ? 'text-accent' : 'text-fg-muted hover:text-fg'
                  }`}
                  style={isActive('/admin') ? { background: 'rgba(239, 68, 68, 0.12)' } : undefined}
                  data-testid="nav-admin"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Admin
                </Link>
              )}
            </nav>
          )}

          {/* Right cluster */}
          <div className="flex items-center gap-2 shrink-0">
            {user && (
              <>
                {/* Points chip */}
                <Link
                  to="/buy-points"
                  className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-semibold border transition hover:border-app-strong"
                  style={{
                    background: 'rgb(var(--card))',
                    borderColor: 'rgb(var(--border))',
                    color: 'rgb(var(--fg))',
                  }}
                  data-testid="points-display"
                  title="Points"
                >
                  <Coins className="w-3.5 h-3.5 text-fg-muted" />
                  {(user.points || 0).toLocaleString('en-IN')}
                </Link>

                {/* Super points chip */}
                {(user.super_points || 0) > 0 && (
                  <Link
                    to="/rewards"
                    className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-bold border transition"
                    style={{
                      background: 'rgba(239, 68, 68, 0.10)',
                      borderColor: 'rgba(239, 68, 68, 0.35)',
                      color: 'rgb(var(--accent))',
                    }}
                    data-testid="super-points-display"
                    title="Super Points — redeemable"
                  >
                    <Star className="w-3.5 h-3.5 fill-current" />
                    {(user.super_points || 0).toLocaleString('en-IN')}
                  </Link>
                )}

                {/* Upgrade / Pro badge */}
                {isPro ? (
                  <span
                    className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider"
                    style={{
                      background: 'linear-gradient(135deg, #F59E0B, #EAB308)',
                      color: '#111',
                    }}
                    data-testid="pro-badge"
                  >
                    <Crown className="w-3 h-3" /> Pro
                  </span>
                ) : (
                  <Link
                    to="/upgrade"
                    className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[13px] font-semibold text-white transition hover:brightness-110"
                    style={{ background: 'rgb(var(--accent))' }}
                    data-testid="header-upgrade-button"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Upgrade
                  </Link>
                )}

                <NotificationBell />

                {/* Theme toggle */}
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-md text-fg-muted hover:text-fg transition"
                  style={{ background: 'transparent' }}
                  data-testid="theme-toggle"
                  aria-label="Toggle theme"
                  title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                >
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>

                {/* Profile dropdown */}
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={() => setProfileOpen(v => !v)}
                    className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-md hover:bg-card-hover transition"
                    data-testid="profile-menu-trigger"
                  >
                    <div
                      className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold overflow-hidden"
                      style={{ background: 'linear-gradient(135deg, #EF4444, #B91C1C)' }}
                    >
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        (user.name || 'U').charAt(0).toUpperCase()
                      )}
                    </div>
                    <ChevronDown className="w-3 h-3 text-fg-muted hidden sm:block" />
                  </button>

                  {profileOpen && (
                    <div
                      className="absolute right-0 mt-2 w-56 rounded-lg border shadow-xl overflow-hidden z-50"
                      style={{ background: 'rgb(var(--bg-elevated))', borderColor: 'rgb(var(--border))' }}
                      data-testid="profile-menu"
                    >
                      <div className="px-3 py-3 border-b" style={{ borderColor: 'rgb(var(--border))' }}>
                        <p className="text-sm font-semibold text-fg truncate">{user.name}</p>
                        <p className="text-xs text-fg-muted truncate">{user.email}</p>
                      </div>
                      <div className="py-1">
                        <Link to="/profile" className="block px-3 py-2 text-sm text-fg-secondary hover:bg-card-hover hover:text-fg transition" data-testid="menu-profile">My Profile</Link>
                        <Link to="/upgrade" className="block px-3 py-2 text-sm text-fg-secondary hover:bg-card-hover hover:text-fg transition" data-testid="menu-upgrade">Subscription</Link>
                        <Link to="/referral" className="block px-3 py-2 text-sm text-fg-secondary hover:bg-card-hover hover:text-fg transition" data-testid="menu-referral">Referral</Link>
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition"
                          style={{ color: 'rgb(var(--accent))' }}
                          data-testid="logout-button"
                        >
                          <LogOut className="w-3.5 h-3.5" /> Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile hamburger */}
                <button
                  onClick={() => setMobileOpen(!mobileOpen)}
                  className="lg:hidden p-2 rounded-md text-fg-muted hover:text-fg transition"
                  data-testid="hamburger-button"
                  aria-label="Menu"
                >
                  {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {user && mobileOpen && (
        <div
          ref={menuRef}
          className="lg:hidden absolute top-full left-0 right-0 border-b shadow-xl z-40"
          style={{
            background: 'rgb(var(--bg-elevated))',
            borderColor: 'rgb(var(--border))',
          }}
          data-testid="mobile-menu"
        >
          <nav className="max-w-[1400px] mx-auto px-4 py-3 grid grid-cols-2 gap-1">
            {NAV_LINKS.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2.5 rounded-md text-sm font-medium transition ${
                  isActive(link.path) ? 'text-accent' : 'text-fg-secondary hover:text-fg'
                }`}
                style={isActive(link.path) ? { background: 'rgba(239, 68, 68, 0.12)' } : undefined}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
            {user.role === 'admin' && (
              <Link
                to="/admin"
                className="col-span-2 px-3 py-2.5 rounded-md text-sm font-medium text-accent flex items-center gap-2"
                style={{ background: 'rgba(239, 68, 68, 0.12)' }}
                data-testid="mobile-nav-admin"
              >
                <Settings className="w-4 h-4" /> Admin
              </Link>
            )}
            {!isPro && (
              <Link
                to="/upgrade"
                className="col-span-2 mt-1 px-4 py-3 rounded-md text-sm font-bold text-white text-center inline-flex items-center justify-center gap-2"
                style={{ background: 'rgb(var(--accent))' }}
                data-testid="mobile-upgrade"
              >
                <Sparkles className="w-4 h-4" /> Upgrade to Pro
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
